interface MultiplierBookIconProps {
  variant: 'pos' | 'neg';
  idSuffix: string;
  className?: string;
  animated?: boolean;
}

const VARIANT_CONFIG = {
  pos: {
    arrowGradient: { y1: '100%', y2: '0%', stop1: '#00C853', stop2: '#69F0AE' },
    arrowPath: 'M80 25 L63 47 L72 47 L72 82 L88 82 L88 47 L97 47 Z',
    mathStroke: '#FBBF24',
    mathFill: '#FBBF24',
  },
  neg: {
    arrowGradient: { y1: '0%', y2: '100%', stop1: '#FF5252', stop2: '#B91C1C' },
    arrowPath: 'M 80 82 L 63 60 L 72 60 L 72 25 L 88 25 L 88 60 L 97 60 Z',
    mathStroke: '#94A3B8',
    mathFill: '#94A3B8',
  },
} as const;

export default function MultiplierBookIcon({ variant, idSuffix, className = 'h-7 w-7', animated = true }: MultiplierBookIconProps) {
  const id = `${variant}-${idSuffix}`;
  const pulseClass = animated ? (variant === 'pos' ? 'arrow-pulse-pos' : 'arrow-pulse-neg') : '';
  const cfg = VARIANT_CONFIG[variant];

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className={className} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`${id}-piGradient`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
        <linearGradient id={`${id}-arrow`} x1="0%" y1={cfg.arrowGradient.y1} x2="0%" y2={cfg.arrowGradient.y2}>
          <stop offset="0%" stopColor={cfg.arrowGradient.stop1} />
          <stop offset="100%" stopColor={cfg.arrowGradient.stop2} />
        </linearGradient>
        <filter id={`${id}-shadow`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="2" floodColor="#000000" floodOpacity="0.35" />
        </filter>
      </defs>

      {/* Pedestal Base */}
      <g filter={`url(#${id}-shadow)`}>
        <path d="M 11 72 L 11 80 A 34 8 0 0 0 79 80 L 79 72 A 34 8 0 0 1 11 72 Z" fill="#1E293B" />
        <ellipse cx="45" cy="72" rx="34" ry="8" fill="#334155" />
        <ellipse cx="45" cy="72" rx="30" ry="6" fill="#475569" />
      </g>

      {/* 3D Pi Symbol */}
      <g filter={`url(#${id}-shadow)`}>
        <g fill="#1E3A8A" transform="translate(0, 4)">
          <path d="M 21 36 C 21 26 29 26 37 26 L 75 26 C 80 26 82 30 79 36 Z" />
          <path d="M 31 36 L 31 71 L 41 71 L 41 36 Z" />
          <path d="M 59 36 L 59 61 C 59 75 73 75 79 68 L 73 78 C 61 82 49 75 49 61 L 49 36 Z" />
        </g>
        <g fill={`url(#${id}-piGradient)`}>
          <path d="M 21 36 C 21 26 29 26 37 26 L 75 26 C 80 26 82 30 79 36 Z" />
          <path d="M 31 36 L 31 71 L 41 71 L 41 36 Z" />
          <path d="M 59 36 L 59 61 C 59 75 73 75 79 68 L 73 78 C 61 82 49 75 49 61 L 49 36 Z" />
        </g>
      </g>

      {/* Math Symbols */}
      <g className={pulseClass} style={{ animationDelay: '0.5s' }} filter={`url(#${id}-shadow)`}>
        <path d="M15 25 L25 25 M20 20 L20 30" stroke={cfg.mathStroke} strokeWidth="3" strokeLinecap="round" fill="none" />
      </g>
      <g className={pulseClass} style={{ animationDelay: '1s' }} filter={`url(#${id}-shadow)`}>
        <path d="M80 15 L90 25 M90 15 L80 25" stroke={cfg.mathStroke} strokeWidth="3" strokeLinecap="round" fill="none" />
      </g>
      <g className={pulseClass} style={{ animationDelay: '1.5s' }} filter={`url(#${id}-shadow)`}>
        <path d="M15 85 L25 85" stroke={cfg.mathStroke} strokeWidth="3" strokeLinecap="round" fill="none" />
        <circle cx="20" cy="80" r="1.5" fill={cfg.mathFill} />
        <circle cx="20" cy="90" r="1.5" fill={cfg.mathFill} />
      </g>

      {/* Arrow */}
      <g filter={`url(#${id}-shadow)`}>
        <path className={pulseClass} d={cfg.arrowPath} fill={`url(#${id}-arrow)`} stroke="#FFFFFF" strokeWidth="3" strokeLinejoin="round" />
      </g>
    </svg>
  );
}
